import React, { useState } from "react";
import { Loader, RefreshCw } from "lucide-react";
import { useAutoConversionAttempts } from "../hooks/useAutoConversion";

const OUTCOMES = {
  covered: { text: "Converted", className: "bg-emerald-100 text-emerald-800" },
  refreshed_covered: { text: "Live balance was enough", className: "bg-emerald-100 text-emerald-800" },
  failed: { text: "Failed", className: "bg-red-100 text-red-800" },
  skipped: { text: "Skipped", className: "bg-amber-100 text-amber-800" },
  needs_approval: { text: "Needs approval", className: "bg-blue-100 text-blue-800" },
  off: { text: "Switched off", className: "bg-gray-100 text-gray-700" },
  not_applicable: { text: "Not applicable", className: "bg-gray-100 text-gray-700" },
};

const FILTERS = [
  ["", "All"],
  ["covered", "Converted"],
  ["needs_approval", "Needs approval"],
  ["skipped", "Skipped"],
  ["failed", "Failed"],
  ["off", "Switched off"],
];

const amount = (value) => {
  const num = Number(value);
  return Number.isFinite(num) && value !== null
    ? num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : "-";
};

const when = (iso) => new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "medium" });

// What the automatic top-up did for each payout that asked for it, so "did it
// even try?" can be answered without the server logs.
function AutoConversionAttempts() {
  const [action, setAction] = useState("");
  const { data, isLoading, isError, error, refetch, isFetching } = useAutoConversionAttempts({ action });

  return (
    <div className="pt-4 mt-4 border-t border-gray-200">
      <div className="flex flex-wrap gap-3 justify-between items-center mb-2">
        <p className="text-sm font-bold text-[#013941]">Recent attempts</p>
        <div className="flex flex-wrap gap-2 items-center">
          {FILTERS.map(([value, label]) => (
            <button
              key={value || "all"}
              onClick={() => setAction(value)}
              className={`px-3 py-1 text-xs font-semibold rounded-full border ${
                action === value ? "text-white bg-[#013941] border-[#013941]" : "text-gray-600 bg-white border-gray-200"
              }`}
            >
              {label}
            </button>
          ))}
          <button onClick={() => refetch()} title="Refresh" className="p-1.5 text-gray-500 rounded-full hover:bg-gray-100">
            <RefreshCw className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {isLoading && (
        <div className="flex gap-2 items-center text-sm text-gray-500">
          <Loader className="w-4 h-4 animate-spin" /> Loading attempts...
        </div>
      )}
      {isError && <p className="text-sm text-red-600">Error: {error.message}</p>}
      {data && data.length === 0 && (
        <p className="text-sm text-gray-500">
          No attempts recorded{action ? " for this filter" : " yet"}. One is recorded each time a payout finds a partner wallet short.
        </p>
      )}
      {data && data.length > 0 && (
        <div className="overflow-x-auto bg-white rounded border border-gray-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50">
                {["When", "Partner", "Needed", "Had", "Outcome", "Why", "Payout reference"].map((label, index) => (
                  <th
                    key={label}
                    className={`px-3 py-2 text-xs font-semibold tracking-wider text-gray-500 uppercase ${
                      index === 2 || index === 3 ? "text-right" : "text-left"
                    }`}
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.map((row) => {
                const outcome = OUTCOMES[row.action] ?? { text: row.action, className: "bg-gray-100 text-gray-700" };
                const d = row.details;
                return (
                  <tr key={row.uuid}>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-600">{when(row.createdAt)}</td>
                    <td className="px-3 py-2 text-gray-900 capitalize">
                      {row.partner.toLowerCase()} <span className="text-xs text-gray-400">{row.balanceCurrency}</span>
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">{amount(row.needed)}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">{amount(row.balance)}</td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap ${outcome.className}`}>
                        {outcome.text}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-gray-700">
                      {row.reason}
                      {d?.fromAmount != null && (
                        <span className="block text-xs text-gray-500">
                          {amount(d.fromAmount)} {d.fromCurrency} to about {amount(d.quotedToAmount)} {d.toCurrency}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-gray-600 break-all">{row.reference}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default AutoConversionAttempts;
